import ArrowForward from '@mui/icons-material/ArrowForward';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import MailOutlined from '@mui/icons-material/MailOutlined';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type MouseEvent, type ReactNode, useState } from 'react';
import { SlackIcon } from '../icons/SlackIcon';
import type {
  AppNotification,
  NotificationCategory,
  NotificationDelivery,
  NotificationTone,
  Role,
} from './types';

const TONE_SX: Record<NotificationTone, { bgcolor: string; color: string }> = {
  risk: { bgcolor: 'error.bg', color: 'error.main' },
  opportunity: { bgcolor: 'success.bg', color: 'success.main' },
  anomaly: { bgcolor: 'secondary.light', color: 'secondary.main' },
  workflow: { bgcolor: 'info.bg', color: 'info.main' },
  system: { bgcolor: 'warning.bg', color: 'warning.dark' },
  info: { bgcolor: 'primary.bg', color: 'primary.main' },
  neutral: { bgcolor: 'background.platform', color: 'text.secondary' },
};

const CATEGORY_META: Record<NotificationCategory, { label: string; color: string }> = {
  performance: { label: 'Performance', color: 'primary.main' },
  anomaly: { label: 'Anomaly & pacing', color: 'secondary.main' },
  opportunity: { label: 'Opportunity', color: 'success.main' },
  portfolio: { label: 'Portfolio', color: 'text.primary' },
  workflow: { label: 'Workflow', color: 'info.main' },
  system: { label: 'System & data', color: 'warning.dark' },
};

interface NotifRowProps {
  notification: AppNotification;
  onAct: (notification: AppNotification) => void;
  onDismiss: (id: string) => void;
}

function NotifRow({ notification, onAct, onDismiss }: NotifRowProps): ReactNode {
  const { Icon } = notification;
  const tone = TONE_SX[notification.tone];
  const category = CATEGORY_META[notification.category];
  const unread = !notification.read;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onAct(notification)}
      sx={{
        position: 'relative',
        display: 'flex',
        gap: 1.5,
        px: 3,
        py: 2.5,
        cursor: 'pointer',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: unread ? (theme) => alpha(theme.palette.primary.main, 0.05) : 'transparent',
        '&:last-of-type': { borderBottom: 0 },
        '&:hover': {
          bgcolor: (theme) => alpha(theme.palette.primary.main, unread ? 0.09 : 0.03),
        },
        '&:hover .notif-dismiss': { opacity: 1 },
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ...tone,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flex: 1 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              color: category.color,
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: category.color }} />
            <Typography variant="overline" sx={{ color: 'inherit' }}>
              {category.label}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', flexShrink: 0 }}>
            {notification.time}
          </Typography>
        </Box>

        <Typography sx={{ fontSize: 13, fontWeight: unread ? 700 : 600, color: 'text.primary' }}>
          {notification.title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{notification.body}</Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mt: 0.5,
            minHeight: 18,
          }}
        >
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            {notification.scope ?? ''}
          </Typography>
          {notification.actionLabel ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: 11.5,
                fontWeight: 700,
                color: 'primary.main',
                flexShrink: 0,
              }}
            >
              {notification.actionLabel}
              <ArrowForward sx={{ fontSize: 12 }} />
            </Box>
          ) : null}
        </Box>
      </Box>

      <ButtonBase
        className="notif-dismiss"
        title="Dismiss"
        onClick={(event) => {
          event.stopPropagation();
          onDismiss(notification.id);
        }}
        sx={{
          position: 'absolute',
          top: 9,
          right: 9,
          width: 22,
          height: 22,
          borderRadius: 1,
          color: 'text.secondary',
          opacity: 0,
          transition: 'opacity .12s, background-color .12s, color .12s',
          '&:hover': { bgcolor: 'error.bg', color: 'error.main' },
        }}
      >
        <Close sx={{ fontSize: 13 }} />
      </ButtonBase>
    </Box>
  );
}

interface NotificationBellProps {
  notifications: AppNotification[];
  role: Role;
  onAct: (notification: AppNotification) => void;
  onMarkAllRead: () => void;
  onDismiss: (id: string) => void;
  /** Channels actually able to deliver; omitted or all false hides the delivery footer. */
  delivery?: NotificationDelivery;
}

export function NotificationBell({
  notifications,
  role,
  onAct,
  onMarkAllRead,
  onDismiss,
  delivery,
}: NotificationBellProps): ReactNode {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const open = Boolean(anchorEl);

  const routed = notifications.filter((notification) => notification.roles.includes(role.id));
  const unread = routed.filter((notification) => !notification.read);
  const list = tab === 'unread' ? unread : routed;

  const act = (notification: AppNotification): void => {
    onAct(notification);
    setAnchorEl(null);
  };

  return (
    <>
      <ButtonBase
        onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        title="Notifications"
        aria-label="Notifications"
        sx={{
          position: 'relative',
          width: 40,
          height: 40,
          borderRadius: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'border.strong',
          color: open ? 'primary.main' : 'text.secondary',
          boxShadow: open ? (theme) => `0 0 0 3px ${theme.palette.primary.bg}` : 'none',
          transition: 'box-shadow .15s, color .15s',
          '&:hover': {
            color: open ? 'primary.main' : 'text.primary',
            boxShadow: open ? (theme) => `0 0 0 3px ${theme.palette.primary.bg}` : 4,
          },
        }}
      >
        <NotificationsNoneOutlined sx={{ fontSize: 19 }} />
        {unread.length > 0 ? (
          <Box
            sx={{
              position: 'absolute',
              top: -5,
              right: -5,
              minWidth: 17,
              height: 17,
              px: '0.25rem',
              borderRadius: '0.5rem',
              bgcolor: 'error.main',
              color: 'common.white',
              border: '2px solid',
              borderColor: 'background.paper',
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {unread.length > 9 ? '9+' : unread.length}
          </Box>
        ) : null}
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { mt: 1, width: 392, maxWidth: 'calc(100vw - 40px)', overflow: 'hidden' } },
          list: { sx: { p: 0 } },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>
              Notifications
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              Routed to {role.name}
            </Typography>
          </Box>
          <ButtonBase
            disabled={unread.length === 0}
            onClick={onMarkAllRead}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: 'primary.main',
              '&.Mui-disabled': { color: 'text.disabled' },
            }}
          >
            Mark all read
          </ButtonBase>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {(['all', 'unread'] as const).map((value) => (
            <ButtonBase
              key={value}
              onClick={() => setTab(value)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'capitalize',
                color: tab === value ? 'common.white' : 'text.secondary',
                bgcolor: tab === value ? 'background.icon' : 'transparent',
                '&:hover': tab === value ? {} : { bgcolor: 'background.platform' },
              }}
            >
              {value}
              <Box component="span" sx={{ opacity: 0.75 }}>
                {value === 'all' ? routed.length : unread.length}
              </Box>
            </ButtonBase>
          ))}
        </Box>

        <Box sx={{ maxHeight: 'min(60vh, 460px)', overflowY: 'auto' }}>
          {list.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                py: 8,
                color: 'text.secondary',
              }}
            >
              <Check sx={{ fontSize: 20, color: 'success.main' }} />
              <Typography sx={{ fontSize: 12.5 }}>You're all caught up</Typography>
            </Box>
          ) : (
            list.map((notification) => (
              <NotifRow
                key={notification.id}
                notification={notification}
                onAct={act}
                onDismiss={onDismiss}
              />
            ))
          )}
        </Box>

        {delivery?.slack || delivery?.email ? (
          <Box
            data-testid="notification-delivery"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
              px: 3,
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.page',
              fontSize: 11,
              color: 'text.secondary',
            }}
          >
            {delivery.slack ? (
              <DeliveryItem icon={<SlackIcon sx={{ fontSize: 14 }} />}>
                Delivering to workspace channel
              </DeliveryItem>
            ) : null}
            {delivery.email ? (
              <DeliveryItem icon={<MailOutlined sx={{ fontSize: 14 }} />}>
                Email delivery on
              </DeliveryItem>
            ) : null}
          </Box>
        ) : null}
      </Menu>
    </>
  );
}

function DeliveryItem({ icon, children }: { icon: ReactNode; children: ReactNode }): ReactNode {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {icon} {children}
    </Box>
  );
}
